import { initI18n } from '@/lib/i18n';

/**
 * BP-11 §13's `notifications.*` namespace.
 *
 * Registered from the feature rather than added to
 * `src/lib/i18n/resources.ts`, for the reason `features/licensing/strings.ts`
 * gives: a feature package owns its copy.
 *
 * **The reader is a client, not an owner.** Nothing here mentions FCM, a
 * token, a device id or a tenant. What a person needs to know on this
 * screen is whether their phone will buzz before their appointment, and —
 * if it will not — what they can do about it, which on Android after a
 * refusal is only ever "open settings".
 *
 * **"Turn off" is not "forget".** Turning notifications off is the OS
 * switch; forgetting a phone deletes the registration, which is what a
 * person wants for a handset they no longer have. Two actions, two
 * sentences, because conflating them is how somebody loses reminders on
 * the phone in their hand while trying to tidy up an old one.
 */
export const NOTIFICATIONS_NAMESPACE = 'notifications';

export const notificationsStrings = {
  nav: { settings: 'Notifications' },
  title: 'Appointment reminders',
  intro:
    'We can send a reminder to this phone the day before your appointment, and again two hours before.',
  state: {
    granted: 'Reminders are on for this phone.',
    prompt: 'Reminders are off for this phone.',
    denied: 'This phone has notifications turned off for 26AD.',
    unsupported: 'Install the 26AD app to get reminders on this phone.',
  },
  action: {
    enable: 'Turn on reminders',
    enabling: 'Asking…',
    settings: 'Open settings',
    forget: 'Forget this phone',
    forgetting: 'Forgetting…',
  },
  // Android gives no second prompt once somebody has refused, so this is
  // the only way back and the screen has to say so plainly.
  deniedHelp:
    'Your phone will not ask again. Open settings, allow notifications for 26AD, then come back here.',
  consent:
    'Reminders also need your consent for appointment messages. You can change that on your privacy screen.',
  // §13: "Version shown in app Settings equals `web/package.json` version of
  // the tagged commit". This is the client app's only settings screen, so it
  // is where the version lives — and the first thing anybody is asked for
  // when they report that reminders stopped arriving.
  version: 'Version {{version}}',
  devices: {
    title: 'Phones you get reminders on',
    empty: 'No phone is registered yet.',
    lastSeen: 'Last used',
    never: 'Never',
    // Rule R6's three reasons. Each says what happened and what to do,
    // because "invalid" on its own tells a person nothing.
    disabled: {
      unregistered: 'Stopped — the app was removed from this phone.',
      invalid: 'Stopped — this phone could not be reached.',
      user: 'Turned off on this phone.',
    },
  },
} as const;

/** Add the namespace to the shared i18next instance. Idempotent. */
export function registerDeviceStrings(): void {
  initI18n().addResourceBundle(
    'en',
    NOTIFICATIONS_NAMESPACE,
    notificationsStrings,
    true,
    false,
  );
}

registerDeviceStrings();
